import { useQuery } from "@tanstack/react-query";
import { cdFetch, cdPost } from "../api";
import { queryKeys } from "../queryKeys";
import { useAppSelector } from "../../services/redux/hooks/hooks";
import { revertTransformWire } from "../../utils/helpers/mapModulesToIcon";

function getReleaseDateTreeData(releaseDateArr: any[]) {
  const months = releaseDateArr
    .map((item: any) => (typeof item === "string" ? item : item.releaseDate))
    .filter(Boolean);

  const grouped: Record<string, { label: string; value: string }[]> = {};
  months.forEach((m) => {
    // Match "Jul 25", "Jul-25", "Jul 2025", "Jul-2025"
    const match = m.match(/^([A-Za-z]{3})[ -]?(\d{2,4})$/);
    if (match) {
      const month = match[1];
      let year = match[2];
      // Convert 2-digit year to 4-digit
      year = year.length === 2 ? `20${year}` : year;
      if (!grouped[year]) grouped[year] = [];
      grouped[year].push({
        label: month, // Only month as label
        value: `${month} ${year}`, // Full "Jul 2025" as value
      });
    }
  });

  const monthOrder = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const sortedYears = Object.keys(grouped).sort(
    (a, b) => Number(b) - Number(a),
  );

  return sortedYears.map((year) => {
    // Sort months in each year
    const sortedChildren = grouped[year].sort(
      (a, b) => monthOrder.indexOf(a.label) - monthOrder.indexOf(b.label),
    );
    return {
      label: year,
      value: sortedChildren.map((child) => child.value),
      children: sortedChildren,
    };
  });
}

export const useWireFiltersData = (filters: any) => {
  const { stockProjects, districts, vendors, releaseDate, contractIds } =
    filters;
  const userDetails = useAppSelector((state) => {
    return state.user;
  });
  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      queryKeys.notifications,
      userDetails?.role_name,
      userDetails?.userEmail,
      stockProjects,
      districts,
      vendors,
      releaseDate,
      contractIds,
    ],
    enabled: !!userDetails?.role_name,
    queryFn: () =>
      getWireFiltersData(
        userDetails?.role_name,
        userDetails?.userEmail,
        filters,
      ),
  });
  const wireFilters = { ...data?.data?.data };
  wireFilters.districts = wireFilters?.districts?.map((item: string) => {
    return { district: item };
  });
  wireFilters.vendors = wireFilters?.vendors?.map((item: string) => {
    return { vendor: item };
  });
  wireFilters.releaseDate = wireFilters?.releaseDate?.map((item: string) => {
    return { releaseDate: item };
  });
  wireFilters.treeReleaseDate =
    wireFilters?.releaseDate?.length > 0
      ? getReleaseDateTreeData(wireFilters.releaseDate)
      : [];
  wireFilters.stockProject = wireFilters?.stockProject?.map((item: string) => {
    return { stockProject: item };
  });

  wireFilters.contractId = wireFilters?.contractId?.map((item: string) => {
    return { contractId: item };
  });

  wireFilters.status = wireFilters?.status?.map((item: string) => {
    return { status: item };
  });

  return { wireFilters, isLoading, refetch };
};

export const useWireContractDetails = (
  stockProject: string[],
  vendor: string[],
  district: string[],
  release_month: string[],
  contractIds: string[],
  status: string[],
) => {
  const shouldFetch =
    !!stockProject ||
    !!district ||
    !!vendor ||
    !!release_month ||
    !!contractIds ||
    !!status;
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      queryKeys.wireContractDetails,
      stockProject,
      vendor,
      district,
      release_month,
      contractIds,
      status,
    ],
    queryFn: () =>
      getWireContractDetails(
        stockProject,
        vendor,
        district,
        release_month,
        contractIds,
        status,
      ),
    staleTime: 20000,
    enabled: shouldFetch,
  });

  return { data, isLoading, error, refetch };
};

const getWireFiltersData = (role: string, userEmail: string, filters: any) => {
  let qParams = "";
  const {
    stockProjects,
    districts,
    vendors,
    releaseDate,
    contractIds,
    status,
  } = filters;
  if (role) {
    qParams += `role=${JSON.stringify(role)}&`;
  }
  if (userEmail) {
    qParams += `userEmail=${JSON.stringify(userEmail)}&`;
  }
  if (districts && districts.length > 0) {
    qParams += `district=${JSON.stringify(districts)}&`;
  }
  if (vendors && vendors.length > 0) {
    qParams += `vendor=${JSON.stringify(vendors)}&`;
  }
  if (contractIds && contractIds.length > 0) {
    qParams += `contractId=${JSON.stringify(contractIds)}&`;
  }
  if (status && status.length > 0) {
    qParams += `status=${JSON.stringify(status)}&`;
  }
  if (releaseDate && releaseDate.length > 0) {
    qParams += `releaseDate=${JSON.stringify(releaseDate)}&`;
  }
  if (stockProjects && stockProjects.length > 0) {
    qParams += `stockProject=${JSON.stringify(stockProjects)}`;
  }
  const res = cdFetch(`/getWireContractFilters?${qParams}`);
  return res;
};

// const getWireContractDetails = (
//   stockProject: string[],
//   vendor: string[],
//   district: string[],
//   release_month: string[],
//   contractIds: string[],
//   status: string[],
// ) => {
//   const revertedMonth = release_month
//     ? revertTransformWire(release_month)
//     : null;
//   let qParams = "";

//   if (stockProject && stockProject.length > 0) {
//     qParams += `stock_project=${JSON.stringify(stockProject)}&`;
//   }
//   if (contractIds && contractIds.length > 0) {
//     qParams += `contract_id=${JSON.stringify(contractIds)}&`;
//   }
//   if (status && status.length > 0) {
//     qParams += `status=${JSON.stringify(status)}&`;
//   }
//   if (vendor && vendor.length > 0) {
//     qParams += `vendor=${JSON.stringify(vendor)}&`;
//   }
//   if (district && district.length > 0) {
//     qParams += `district=${JSON.stringify(district)}&`;
//   }
//   if (release_month && release_month.length > 0) {
//     qParams += `release_month=${JSON.stringify(revertedMonth)}&`;
//   }

//   if (qParams.length > 0) {
//     qParams = "?" + qParams.slice(0, -1);
//     return cdFetch(`/getWireContractDetails${qParams}`);
//   }
// }

const getWireContractDetails = (
  stockProject: string[],
  vendor: string[],
  district: string[],
  release_month: string[],
  contractIds: string[],
  status: string[],
) => {
  const revertedMonth = release_month
    ? revertTransformWire(release_month)
    : null;
  const body = {
    stock_project: stockProject,
    vendor,
    district,
    release_month: revertedMonth,
    contract_id: contractIds,
    status,
  };

  // Only make the call if at least one filter is not empty
  const allEmpty = Object.values(body).every(
    (val) => val == null || (Array.isArray(val) && val.length === 0),
  );
  if (allEmpty) return null;

  return cdPost("/getWireContractDetails", body);
};
